import {
  BoxType,
  GroupPeopleType,
  ImageFrameRole,
  isPersonMarkupField,
  isRosterCellRole,
  personOwnsGroupText,
  type CustomGroupTemplateBox,
  type CustomTemplateBox,
} from '@lite-workspace/dto-fotoclass-templates';

import { BookData } from '../interfaces/book-data.interface';
import { SpreadData } from '../interfaces/spread-data.interface';
import {
  DrawableData,
  DrawableGroupData,
  GroupBoxParams,
  isDrawableGroup,
  isDrawableImage,
  isDrawableText,
} from '../interfaces/drawable-data.interface';
import {
  VIGNETTE_SPREAD_TYPE,
  containedDrawables,
  getSpreadGroups,
  isVignetteSurface,
  soleStudentGroup,
  type VignetteMarkedSpread,
} from './personal-spread-queries';

/** Поверхность глазами рассадки — состав рамок и ничего про тип разворота. */
export type RosterSurface = Parameters<typeof getSpreadGroups>[0];

/** Что мерка ростерной ячейки спрашивает у рамки: только её идентичность и роль. */
export interface RosterCellLike {
  readonly id?: string;
  readonly params?: { readonly peopleType?: string | null } | null;
}

/** К роли добавляется привязка: без неё гидрация человека не находит и кадра не будет. */
export interface HydratedCellLike extends RosterCellLike {
  readonly params?: { readonly peopleType?: string | null; readonly personId?: string | null } | null;
}

/**
 * ЕДИНСТВЕННЫЙ вход переходного моста «одинокая `student`-рамка — якорь владельца или ячейка ростера».
 * Меряется ТИПОМ разворота, который лежит в самой модели: слот `slot_type` приезжает с раскладкой и с
 * типом расходится, а гидрация, вырезание, генератор и миграция читают модель — разойдись они по ширине,
 * миграция сняла бы роль там, куда гидрация фото не ставит. Снимается вместе с мостом после миграции.
 * Пометка приходит частичной: обложка `spread_type` не носит и ростерной не бывает.
 */
export function isRosterSurface(surface: Partial<Pick<SpreadData, 'spread_type'>> | null | undefined): boolean {
  return surface?.spread_type === VIGNETTE_SPREAD_TYPE;
}

/** Рассадка освобождает место пустой строкой — читаем оба написания «пусто». Обе персист-формы несут одинаковый params. */
export function groupPersonId(group: Pick<DrawableGroupData, 'params'>): string | null {
  const raw = group.params?.personId;
  return raw ? raw : null;
}

/**
 * Своя карточка ученика на виньетке: id группы, чья персона — владелец альбома. ЕДИНСТВЕННАЯ мерка
 * «своя ячейка»: её спрашивают гейт дропа, вуаль на холсте, «Заменить фото» (`selectionOwnPersonCell`)
 * и вердикт посадки ученика на виньетку (`photobook-shared`). Мерка виньетки — `isVignetteSurface`:
 * тип разворота ИЛИ слот. Обе персист-формы: группы верхнего уровня коллажа.
 */
export function ownVignetteCellId(
  spread: (VignetteMarkedSpread & { readonly collages?: readonly unknown[] }) | null | undefined,
  ownerPersonId: string | null | undefined,
): string | null {
  if (!ownerPersonId || !spread || !isVignetteSurface(spread)) return null;
  for (const collage of spread.collages ?? []) {
    for (const drawable of containedDrawables(collage)) {
      if (isDrawableGroup(drawable) && groupPersonId(drawable) === ownerPersonId) return drawable.id;
    }
  }

  return null;
}

/** Человека принимает только ростерная роль: у владельца привязки нет по построению. */
export function roleAcceptsPerson(peopleType: string | null | undefined): boolean {
  return isRosterCellRole(peopleType);
}

/** Занятые на ВСЕЙ поверхности: виньетка делит класс между страницами одного разворота. */
export function seatedPersonIds(surface: RosterSurface): ReadonlySet<string> {
  const ids = new Set<string>();
  for (const group of getSpreadGroups(surface)) {
    if (!roleAcceptsPerson(group.params?.peopleType)) continue;
    const personId = groupPersonId(group);
    if (personId) ids.add(personId);
  }

  return ids;
}

/** Одинокая рамка «ученик» на поверхности — рамка владельца альбома, человека в неё не сажают. */
export function isSeatableRosterCell(surface: RosterSurface, group: DrawableGroupData | null | undefined): boolean {
  if (!group || !roleAcceptsPerson(group.params?.peopleType)) return false;

  return group.id !== soleStudentGroup(surface)?.id;
}

/** Вне виньетки одинокая рамка «ученик» — владелец альбома, не ростер; мост до миграции её роли в `owner`. */
export function ownerAnchorGroupId(surface: RosterSurface, rosterSurface: boolean): string | null {
  return rosterSurface ? null : (soleStudentGroup(surface)?.id ?? null);
}

/**
 * Ростерная сетка ГЛУБЖЕ верхнего уровня. «Одинокость» рамки владельца считается нерекурсивным обходом
 * (`soleStudentGroup`), поэтому под такой раскладкой одинокой объявляется ЧУЖАЯ ячейка.
 */
function hasNestedRosterGroups(surface: RosterSurface): boolean {
  // Тот же дуальный вход, что у `getSpreadGroups`: найдись якорь на мигрированной книге, а этот
  // охранник останься legacy-слепым — роль одноклассника переписалась бы необратимо.
  const walk = (drawables: readonly DrawableData[], nested: boolean): boolean =>
    drawables.some(
      d => isDrawableGroup(d) && ((nested && isRosterCellRole(d.params?.peopleType)) || walk(containedDrawables(d), true)),
    );

  return (surface.collages ?? []).some(collage => walk(containedDrawables(collage), false));
}

/**
 * Якорь владельца, роль которого писателю ПОЗВОЛЕНО переписать в разметке. От `ownerAnchorGroupId`
 * отличается ровно охранником вложенной ростерной сетки: чтение моста обратимо и на следующем проходе
 * пересчитывается, а роль в разметке необратима — ошибись мерка «одинокости» на вложенной сетке класса,
 * и рамка одноклассника перестала бы принимать людей навсегда.
 * ЕДИНСТВЕННОЕ определение на ОБОИХ писателей роли: генератор сборки и скрипт миграции. Оставь его
 * в одном из них — и одна и та же раскладка получила бы разную роль в старых и новых книгах.
 */
export function promotableOwnerAnchorGroupId(surface: RosterSurface, rosterSurface: boolean): string | null {
  if (hasNestedRosterGroups(surface)) return null;

  return ownerAnchorGroupId(surface, rosterSurface);
}

/**
 * ЕДИНСТВЕННАЯ мерка «ячейка ростера» — её обязаны читать и гидрация, и вырезание, и счётчики.
 * Разойдись они, вырезанное фото не вернулось бы, а гейт назвал бы пустой рамку с кадром.
 * Якорь считается один раз на поверхность: `soleStudentGroup` обходит её целиком.
 */
export function rosterCellPredicate(surface: RosterSurface, rosterSurface: boolean): (group: RosterCellLike) => boolean {
  const anchorGroupId = ownerAnchorGroupId(surface, rosterSurface);
  return group => isRosterCellRole(group.params?.peopleType) && group.id !== anchorGroupId;
}

/** Ячейка ростера, чей кадр держит карточка персоны: ростерная роль минус рамка владельца. */
export function isRosterCell(surface: RosterSurface, rosterSurface: boolean, group: DrawableGroupData): boolean {
  return rosterCellPredicate(surface, rosterSurface)(group);
}

/**
 * ЕДИНСТВЕННАЯ мерка «в эту ячейку гидрация поставит кадр» — ростерная ячейка ПЛЮС живая привязка.
 * Без привязки `resolveGroupPerson` возвращает `undefined` и кадра не появится, поэтому вычесть такую
 * рамку из счёта пустых значит пропустить в печать видимую дыру, а объявить её файл свободным — дать
 * удалить файл, на который книга продолжает ссылаться.
 */
export function hydratedRosterCellPredicate(
  surface: RosterSurface,
  rosterSurface: boolean,
): (group: HydratedCellLike) => boolean {
  const isCell = rosterCellPredicate(surface, rosterSurface);
  return group => isCell(group) && !!group.params?.personId;
}

/**
 * Сколько ячеек книги гидрация обязана посадить на персон. Считается ТОЙ ЖЕ меркой, что и сама гидрация:
 * счётчик, разошедшийся с ней, объявил бы книгу пустой ровно там, где она печатается заполненной.
 *
 * Нужен диагностике «персон не пришло вовсе»: гидрация с пустым списком не запускается и ни об одной
 * осиротевшей привязке не сообщает, а виньетка уходит в печать тем, что застыло в модели.
 * Считает ЛЮБУЮ поверхность, включая обложку: ячейку персоны рисует разметка, а не тип разворота.
 */
export function countSeatedRosterCells(book: Pick<BookData, 'cover' | 'spreads'>): number {
  const onSurface = (surface: RosterSurface | null | undefined, rosterSurface: boolean): number => {
    if (!surface) return 0;
    const isSeated = hydratedRosterCellPredicate(surface, rosterSurface);
    return getSpreadGroups(surface).filter(group => isSeated(group)).length;
  };

  let seated: number = onSurface(book.cover, false);
  for (const spread of book.spreads ?? []) {
    seated += onSurface(spread, isRosterSurface(spread));
  }

  return seated;
}

/**
 * ЧТО ПИШЕТСЯ в персист-форму как «место свободно». Тип поля допускает оба написания
 * (`groupPersonId` читает и `''`, и `null`), а писатель обязан быть один: разойдись он, и «свободно»
 * существовало бы в репозитории в двух орфографиях, а следующий гейт сравнил бы их строкой.
 *
 * Тип берётся у самого поля, а не выводится: под `strictNullChecks: false` (сборка `auth`) голый
 * `= null` вырождается в `any` и валит `noImplicitAny`, а аннотация полем держит одно чтение и у
 * писателя, и у сравнения.
 */
export const EMPTY_SEAT_PERSON_ID: GroupBoxParams['personId'] = null;

/**
 * Место, которое рассадка вправе переносить и освобождать. Мерка ТА ЖЕ, что у генератора сегодня —
 * `transferable` в `seatingFromMaster` и `occupied` в `keepsMasterSeating` фильтруют ровно так.
 * Шире `roleAcceptsPerson` ровно на `groupWithOwner`: рамку «я с группой» позиционный перенос
 * образца сегодня занимает наравне с ростерными, и сузить мерку здесь значило бы тихо поменять,
 * КАКИЕ рамки считаются рассаженными, поверх правки про то, СКОЛЬКО их должно быть живо.
 */
export function isSeatTransferableRole(peopleType: string | null | undefined): boolean {
  return peopleType !== GroupPeopleType.FREE && peopleType !== GroupPeopleType.OWNER;
}

/** Занятое место рассадки: переносимая роль плюс привязка. Пустая рамка — штатный запас шаблона, не посадка. */
export function isSeatedRosterCell(group: Pick<DrawableGroupData, 'params'>): boolean {
  return isSeatTransferableRole(group.params?.peopleType) && groupPersonId(group) !== null;
}

/**
 * Рассаживал ли поверхность ЧЕЛОВЕК — и осталось ли этой рассадке кого сохранять. ХОТЬ ОДНО занятое
 * место, чья персона жива, ⇒ рассадку не пересобираем из ростера: мёртвая привязка стоит своей
 * ячейки, а не всей сетки (этот тикет).
 *
 * ГРАНИЦА, обязательная к сохранению: поверхность, где занятых мест нет вовсе ИЛИ все они мертвы,
 * по-прежнему собирается из ростера. Вечно пустая поверхность хуже актуального класса — это
 * продуктовое решение, а не следствие реализации.
 *
 * Историческая страховка FKS-10311 не снимается, а переезжает с коллажа на ячейку: раньше «все живы»
 * защищало от рассадки, ссылающейся в никуда, теперь от неё защищает освобождение самой ячейки.
 */
export function hasManualSeating(
  groups: readonly Pick<DrawableGroupData, 'params'>[],
  alivePersonIds: ReadonlySet<string>,
): boolean {
  return groups.some(group => {
    if (!isSeatedRosterCell(group)) return false;
    const personId = groupPersonId(group);

    return !!personId && alivePersonIds.has(personId);
  });
}

/**
 * Место образца, чья персона выбыла из проекта. Адресуется ПЕРСОНОЙ, а не индексом: позиция места
 * в переносе рассадки и так считается позиционно, и «просто пустое место» от «место человека,
 * которого больше нет» отличается только этим полем — без него телеметрия сироты слепнет.
 */
export interface DeadSeat {
  readonly deadPersonId: string;
}

/** Отличает мёртвое место от персоны и от штатно пустого места: у персоны такого поля нет. */
export function isDeadSeat(seat: unknown): seat is DeadSeat {
  return !!seat && typeof seat === 'object' && typeof (seat as DeadSeat).deadPersonId === 'string';
}

/**
 * ЕДИНСТВЕННАЯ орфография «место свободно» на репозиторий: привязка снята, подписи прежнего жильца
 * пусты, запечённый кадр снят. Чистая функция — исходник не мутируется, повторный прогон на уже
 * освобождённой ячейке даёт эквивалентное состояние (идемпотентность записи 1B2).
 *
 * Кадр снимается ЗДЕСЬ и только здесь. Переносить это в общий гейт записи модели
 * (`PhotobookModelWriteGateService` / `sanitizeVignettePhotoLeak`) ЗАПРЕЩЕНО поправкой ADR-0018 от
 * 2026-08-22: там гашение срабатывало бы на ячейке ЖИВОЙ персоны без `vignetteImage`, и на первом
 * же несвязанном сохранении видимый портрет ученика превращался бы в пустую рамку. Здесь снимать
 * можно ровно потому, что персоны в ячейке уже не осталось: подставлять на её место нечего.
 *
 * Дети обходятся НЕрекурсивно — той же меркой, что `fillPersonTexts` их заполняет, а гидрация
 * (`applyVignetteImageToGroup`) ставит кадр в КАЖДУЮ картинку ячейки, поэтому и снимаются все.
 *
 * ГАСИТСЯ РОВНО ТО, ЧТО ЗАПОЛНЕНИЕ И ПИСАЛО. Мерка одна на обе половины пары — `personOwnsGroupText`
 * (dto-fotoclass-templates), тот же закон, по которому `resolvePersonTextsPositional` решает, что
 * положить. Стирать шире значит уничтожать то, чего вернуть нечем:
 *   • `freeText` и безымянный бокс в РАЗМЕЧЕННОЙ группе — `TextScope.ALBUM`, живут только в модели
 *     книги, второго источника нет по построению (`applySettingsToModel` их исключает явно);
 *   • свободное поле обложки, которое несёт запомненный текст альбома (`carryCoverTexts`), — тот же
 *     случай: назначения у него нет, и `restateCoverProjectTexts` его не вернёт;
 *   • школа / класс / город / год выпуска — `TextScope.SHARED`, их переставляет проект на каждом
 *     чтении, но лишняя запись их всё равно теряет при пустых настройках проекта.
 * Персональные тексты (ФИО, отчество, цитата, предмет) и безымянные боксы НЕразмеченной группы —
 * законная цель: их пишет карточка персоны, которой в ячейке больше нет.
 *
 * ЗАКРЕПЛЁННЫЙ КАДР НЕ СНИМАЕТСЯ. `ImageFrameRole.PINNED` — выбор фотографа, и вернуть его нечем:
 * `fillEmptyImageSlots` заполняет рамку только при точном совпадении роли, а `PINNED` не подаётся
 * туда как цель ни одним вызовом. Сам генератор защищает закреп семью отдельными охранниками —
 * освобождение обязано быть восьмым, иначе оно доезжает до контентных разворотов и обложки
 * (`reassignOwnerGroupPersons`) и снимает там то, что все семеро щадят.
 */
export function releaseRosterCell(group: DrawableGroupData): DrawableGroupData {
  const children = group.drawableObjects;
  // «Дизайнер разметил группу» — ровно то же условие, что читает заполнение (`isPersonMarkupField`):
  // есть хоть один текст ПОЗИЦИОННОГО персонального типа. Считается один раз на группу, как и там.
  const hasPersonMarkup = children.some(child => isDrawableText(child) && isPersonMarkupField(child.textType));

  return {
    ...group,
    params: { ...group.params, personId: EMPTY_SEAT_PERSON_ID },
    drawableObjects: children.map(child => {
      if (isDrawableText(child)) {
        return personOwnsGroupText(child.textType, hasPersonMarkup) ? { ...child, text: { ...child.text, text: '' } } : child;
      }

      if (isDrawableImage(child)) {
        return child.frameRole === ImageFrameRole.PINNED ? child : { ...child, image: null };
      }

      return child;
    }),
  };
}

// ─── v1-твин освобождения ячейки (custom-template-v1, CustomGroupTemplateBox) ────────────────────
// Две ветви — состояние перехода: v0-ветку выше держат её живые вызывающие на `DrawableGroupData`,
// и она уйдёт с последним из них.

/**
 * v1-твин {@link releaseRosterCell}. Закон «что гасится» ОБЩИЙ и не повторяется здесь ни одной
 * строкой: разметка группы и принадлежность текста считаются теми же `isPersonMarkupField` /
 * `personOwnsGroupText`, закреп кадра — тем же `ImageFrameRole.PINNED`. Расходится только АДРЕС полей:
 * у боксовой формы подпись лежит в `params.text`, тип — в `params.textType`, кадр — в `params.assignedPhoto`.
 *
 * Кадр снимается ДВУМЯ полями: `assignedPhoto` держит сам файл, `photoPlacementRect` — кадрирование
 * под него. Оставленный rect осиротеет — следующее чтение встретит его `orphan-photo-placement-rect`,
 * а v0-круг его теряет сам: кодек пишет rect только под живой кадр (`boxes/image-box.ts`).
 *
 * БОКСОВАЯ ЗАПИСЬ «место свободно» здесь НЕ первая, и это надо назвать: `clearPersonCell`
 * (`template-document/src/lib/persons.ops.ts`) снимает ячейку прямо на `PersistedSpreadV1` уже
 * сегодня. Расходятся три правила — она НЕ щадит `ImageFrameRole.PINNED`, гасит ВСЕ тексты ячейки
 * (а не только `personOwnsGroupText`) и удаляет ключ `photoPlacementRect` вместо `undefined`
 * (на персисте неразличимо). Расхождение ПРЕ-СУЩЕСТВУЕТ у v0 — ровно теми же тремя пунктами между
 * `spread-edit.ops.ts` (редактор) и `releaseRosterCell` (генерация), — и этот твин честно зеркалит
 * генераторную ветку, а не заводит четвёртый закон. Свести две ветви можно только вместе: пока
 * редактор и генерация «освобождают» по разным правилам про PINNED, переиспользовать было
 * нечего — но нечего именно поэтому, а не потому, что боксовой записи не существовало.
 */
export function releaseRosterCellBoxes(group: CustomGroupTemplateBox): CustomGroupTemplateBox {
  const children = group.boxes ?? [];
  const hasPersonMarkup = children.some(child => child.type === BoxType.TEXT && isPersonMarkupField(child.params.textType));

  return {
    ...group,
    params: { ...group.params, personId: EMPTY_SEAT_PERSON_ID },
    boxes: children.map((child): CustomTemplateBox => {
      if (child.type === BoxType.TEXT) {
        return personOwnsGroupText(child.params.textType, hasPersonMarkup)
          ? { ...child, params: { ...child.params, text: '' } }
          : child;
      }

      if (child.type === BoxType.IMAGE) {
        return child.params.frameRole === ImageFrameRole.PINNED
          ? child
          : { ...child, params: { ...child.params, assignedPhoto: null, photoPlacementRect: undefined } };
      }

      return child;
    }),
  };
}

/**
 * Ячейки поверхности, посаженные на персону, которой в проекте больше НЕТ. Мерка та же, что у
 * гидрации (`hydratedRosterCellPredicate`): разойдись они, редактор пометил бы бейджем рамку,
 * в которую гидрация кадр как раз ставит.
 *
 * Пустой живой список молчит: гидрация без персон не запускается вовсе, и объявить по нему всю
 * книгу мёртвой значило бы зажечь бейдж на каждой ячейке любого ещё не загруженного проекта.
 */
export function danglingRosterCellIds(
  surface: RosterSurface,
  rosterSurface: boolean,
  alivePersonIds: ReadonlySet<string>,
): string[] {
  if (alivePersonIds.size === 0) return [];
  const isHydrated = hydratedRosterCellPredicate(surface, rosterSurface);
  const ids: string[] = [];
  for (const group of getSpreadGroups(surface)) {
    if (!isHydrated(group)) continue;
    const personId = groupPersonId(group);
    if (personId && !alivePersonIds.has(personId)) ids.push(group.id);
  }

  return ids;
}

/** Виньетка глазами мерки «фото вне карточек»: её пометки и коллажи в любой персист-форме. */
export type VignetteFramesSurface = VignetteMarkedSpread & { readonly collages?: readonly unknown[] | null };

/**
 * Фоторамки виньетки ВНЕ карточек класса: ни один предок рамки не несёт ростерной роли. ЕДИНСТВЕННАЯ мерка
 * «фото вне виньетки» — её читают ось плашки, гейты редактора, приватность и занятость кадра. Судит только
 * роль (`isRosterCellRole`), а не мост одинокой рамки: на виньетке своя карточка ученика остаётся карточкой.
 * Рамки «Мой портрет», «Я с группой» и свободных групп сюда входят, рамки-наполнители сборки
 * (`isVignetteFillerFrame`) — нет; не виньетка — пусто. Обе персист-формы.
 */
export function vignetteOutsidePhotoFrames(surface: VignetteFramesSurface | null | undefined): DrawableData[] {
  const frames: DrawableData[] = [];
  if (!surface || !isVignetteSurface(surface)) return frames;
  const walk = (drawables: readonly DrawableData[]): void => {
    for (const drawable of drawables) {
      if (isDrawableImage(drawable)) {
        if (!isVignetteFillerFrame(drawable)) frames.push(drawable);
      } else if (isDrawableGroup(drawable) && !isRosterCellRole(drawable.params?.peopleType)) walk(containedDrawables(drawable));
    }
  };

  for (const collage of surface.collages ?? []) walk(containedDrawables(collage));
  return frames;
}

/**
 * Хвосты id рамок-наполнителей, которые ставит сама сборка виньетки: групповое фото класса (первая и вторая
 * рамка) и фото свободной страницы. Кадр в них ставит сборка (`seatFillerPhoto`) — при сборке виньетки и
 * смене её шаблона; применение шаблона к одной странице (`setVignettePageTemplate`) его не пересаживает.
 * Выбор этих кадров принадлежит сборке, поэтому ученику рамки закрыты и в мерку «фото вне карточек» не входят.
 * Те же хвосты, что `GROUP_PHOTO_SUFFIX`, `BASE_SECOND_GROUP_PHOTO_SUFFIX` и `FREE_PAGE_PHOTO_SUFFIX`
 * в photobook-shared (сверка — спека там).
 */
export const VIGNETTE_FILLER_FRAME_SUFFIXES = ['-group-photo', '-group-photo-2', '-free-page-photo'] as const;

/** Рамка-наполнитель сборки виньетки: хвост id шаблона (у v1-бокса без своего — сам id). */
export function isVignetteFillerFrame(frame: { readonly id?: unknown; readonly idFromTemplate?: unknown }): boolean {
  const templateId = typeof frame.idFromTemplate === 'string' ? frame.idFromTemplate : frame.id;
  return typeof templateId === 'string' && VIGNETTE_FILLER_FRAME_SUFFIXES.some(suffix => templateId.endsWith(suffix));
}

/** Рамка `boxId` — фото вне карточек класса на виньетке (`vignetteOutsidePhotoFrames`). */
export function isVignetteOutsidePhotoFrame(surface: VignetteFramesSurface | null | undefined, boxId: string): boolean {
  return vignetteOutsidePhotoFrames(surface).some(frame => frame.id === boxId);
}

/**
 * Есть ли на виньетке фото вне карточек, которое ученику в принципе можно менять: закреплённый кадр не меняет
 * никто, кроме фотографа, и тоггл ради него обещал бы правку, которой не будет.
 */
export function hasVignetteOutsidePhotoFrames(surface: VignetteFramesSurface | null | undefined): boolean {
  return vignetteOutsidePhotoFrames(surface).some(frame => frameRoleOf(frame) !== ImageFrameRole.PINNED);
}

/** Роль рамки в обоих написаниях: legacy держит её на самом drawable, `custom-template-v1` — в `params`. */
function frameRoleOf(frame: DrawableData): string | undefined {
  const record = frame as unknown as { frameRole?: string; params?: { frameRole?: string } };
  return record.frameRole ?? record.params?.frameRole;
}
